/** Minimal queue row shape for portal house duration normalization. */
export interface TPortalHouseDurationFrame {
  id?: string
  duration?: number
  playlistUrn?: string
}

/** Editorial stills shorter than this are extended up to the inferred CPS slot length. */
const SHORT_EDITORIAL_HOUSE_MAX_SECONDS = 4

function isBookCpsFrameId(id: string | undefined): boolean {
  return typeof id === 'string' && id.startsWith('book-')
}

function portalHouseEditorialPlaylistUrns(queue: TPortalHouseDurationFrame[]): Set<string> {
  const byUrn = new Map<string, { editorial: boolean, book: boolean }>()
  for (const row of queue) {
    const urn = row.playlistUrn?.trim() ?? ''
    if (urn === '') {
      continue
    }
    const entry = byUrn.get(urn) ?? { editorial: false, book: false }
    if (isBookCpsFrameId(row.id)) {
      entry.book = true
    }
    else if (row.id) {
      entry.editorial = true
    }
    byUrn.set(urn, entry)
  }
  const out = new Set<string>()
  for (const [urn, flags] of byUrn) {
    if (flags.editorial && !flags.book) {
      out.add(urn)
    }
  }
  return out
}

/**
 * CPS slot length from materialized `book-*` frames: the most common duration (tail pads, house
 * slots). Do not use {@link Math.max} — booked creatives can be longer than one slot (e.g. 13s in 10s slots).
 */
function modeBookCpsDurationSeconds(durations: number[]): number | null {
  if (durations.length === 0) {
    return null
  }
  const counts = new Map<number, number>()
  for (const d of durations) {
    counts.set(d, (counts.get(d) ?? 0) + 1)
  }
  let best: number | null = null
  let bestCount = 0
  for (const [d, c] of counts) {
    if (c > bestCount || (c === bestCount && best != null && d < best)) {
      best = d
      bestCount = c
    }
  }
  return best
}

/**
 * Portal bookable screens play the operator house playlist at CPS slot length, not raw editorial
 * asset duration (e.g. 13s video in a 5s slot). Infer slot seconds from the mode of `book-*` CPS frames.
 */
export function inferPortalCpsSlotDurationSeconds(queue: TPortalHouseDurationFrame[]): number | null {
  const bookDurations = queue
    .filter(f => isBookCpsFrameId(f.id))
    .map(f => f.duration)
    .filter((d): d is number => typeof d === 'number' && d > 0 && d <= 120)
  return modeBookCpsDurationSeconds(bookDurations)
}

/** Mode duration of editorial house frames on dedicated house playlist rows (portal book service list). */
function inferHouseEditorialSlotDurationSeconds(
  queue: TPortalHouseDurationFrame[],
  houseUrns: Set<string>
): number | null {
  const houseDurations = queue
    .filter((f) => {
      const urn = f.playlistUrn?.trim() ?? ''
      return urn !== '' && houseUrns.has(urn) && f.id && !isBookCpsFrameId(f.id)
    })
    .map(f => f.duration)
    .filter((d): d is number => typeof d === 'number' && d > 0 && d <= 120)
  return modeBookCpsDurationSeconds(houseDurations)
}

export function normalizePortalHouseEditorialDurations<T extends TPortalHouseDurationFrame>(
  queue: T[]
): T[] {
  /** Portal Book interleaved: house + paid order rows keep operator/creative durations as published. */
  if (queue.some(f => isBookCpsFrameId(f.id))) {
    return queue
  }
  const houseUrns = portalHouseEditorialPlaylistUrns(queue)
  if (houseUrns.size === 0) {
    return queue
  }
  const bookSlotSeconds = inferPortalCpsSlotDurationSeconds(queue)
  const houseSlotSeconds = inferHouseEditorialSlotDurationSeconds(queue, houseUrns)
  /**
   * Cap long house assets only from CPS `book-*` slot inference — never from the house editorial
   * mode alone. Operator master playlists (registration-self-root) have no book rows; capping from
   * house mode wrongly clips full-length videos (e.g. 58s/125s/81s) to a 5s still slot.
   */
  const capSlotSeconds = bookSlotSeconds
  /**
   * Extend short house stills only to the inferred **house** editorial slot — not the book CPS slot.
   * (Book slots can be longer; stretching 2s HF frames to 3s book CPS made timers and playback wrong.)
   */
  const extendSlotSeconds = houseSlotSeconds ?? bookSlotSeconds
  if (capSlotSeconds == null && extendSlotSeconds == null) {
    return queue
  }
  return queue.map((row) => {
    const urn = row.playlistUrn?.trim() ?? ''
    if (urn === '' || !houseUrns.has(urn) || !row.id || isBookCpsFrameId(row.id)) {
      return row
    }
    const cur = row.duration ?? 0
    // Only cap long house assets (e.g. 13s video); do not pull shorter editorial frames (3s) down to mode slot (2s).
    if (
      capSlotSeconds != null
      && cur > capSlotSeconds
      && cur > SHORT_EDITORIAL_HOUSE_MAX_SECONDS
    ) {
      return { ...row, duration: capSlotSeconds }
    }
    if (
      extendSlotSeconds != null
      && cur > 0
      && cur <= SHORT_EDITORIAL_HOUSE_MAX_SECONDS
      && cur < extendSlotSeconds
    ) {
      return { ...row, duration: extendSlotSeconds }
    }
    return row
  })
}
